// @ts-nocheck - Complex type issues
/**
 * Progress Event Bus System
 *
 * Central event coordination system for real-time progress updates.
 * Replaces manual refresh delays with immediate event-driven updates.
 *
 * Phase 1 Implementation - Real-Time Event System Foundation
 */

export interface ProgressEvent {
  type: 'vocabulary-learned' | 'vocabulary-reviewed' | 'xp-gained' | 'daily-stats-updated';
  data: {
    word?: string;
    masteryChange?: 'new' | 'improved' | 'maintained' | 'declined';
    xpGained?: number;
    userId: string;
    languageCode: string;
    timestamp: string;
    // Additional context for Phase 2+ implementations
    srsCardId?: string;
    oldInterval?: number;
    newInterval?: number;
    difficulty?: number;
    sessionId?: string;
  };
}

export type ProgressEventCallback = (event: ProgressEvent) => void;
export type UnsubscribeFunction = () => void;

/**
 * Singleton Event Bus for Progress Coordination
 *
 * Provides real-time communication between SRS processing and UI components.
 * All progress-related events flow through this central hub.
 */
class ProgressEventBus {
  private static instance: ProgressEventBus;
  private callbacks = new Set<ProgressEventCallback>();
  private eventHistory: ProgressEvent[] = [];
  private maxHistorySize = 50;

  private constructor() {
    // Private constructor for singleton pattern
  }

  static getInstance(): ProgressEventBus {
    if (!ProgressEventBus.instance) {
      ProgressEventBus.instance = new ProgressEventBus();
    }
    return ProgressEventBus.instance;
  }

  /**
   * Emit a progress event to all subscribers
   */
  emit(event: ProgressEvent): void {
    console.group('📢 PROGRESS EVENT BUS: Emitting Event');
    console.log('Event Type:', event.type);
    console.log('Event Data:', event.data);
    console.log('Active Subscribers:', this.callbacks.size);

    if (this.callbacks.size === 0) {
      console.warn('⚠️ WARNING: No subscribers to receive this event!');
    }

    // Add to event history for debugging
    this.eventHistory.push(event);
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory = this.eventHistory.slice(-this.maxHistorySize);
    }

    // Notify all subscribers
    let successfulCallbacks = 0;
    this.callbacks.forEach((callback, index) => {
      try {
        console.log(`📞 Calling subscriber ${index + 1}/${this.callbacks.size}`);
        callback(event);
        successfulCallbacks++;
      } catch (error) {
        console.error(`❌ Subscriber ${index + 1} failed:`, error);
        // Continue processing other callbacks even if one fails
      }
    });

    console.log(`✅ Event delivery complete: ${successfulCallbacks}/${this.callbacks.size} successful`);
    console.groupEnd();
  }

  /**
   * Subscribe to progress events
   */
  subscribe(callback: ProgressEventCallback): UnsubscribeFunction {
    this.callbacks.add(callback);
    console.log('📡 Progress Event Subscriber Added. Total:', this.callbacks.size);

    return () => {
      this.callbacks.delete(callback);
      console.log('📡 Progress Event Subscriber Removed. Total:', this.callbacks.size);
    };
  }

  /**
   * Get recent event history (for debugging)
   */
  getEventHistory(): ProgressEvent[] {
    return [...this.eventHistory];
  }

  /**
   * Clear all subscribers (for testing/cleanup)
   */
  clearSubscribers(): void {
    this.callbacks.clear();
    console.log('🧹 All progress event subscribers cleared');
  }

  /**
   * Get current subscriber count (for monitoring)
   */
  getSubscriberCount(): number {
    return this.callbacks.size;
  }
}

// Export singleton instance
export const progressEventBus = ProgressEventBus.getInstance();

/**
 * Helper functions for common event patterns
 */
export const ProgressEventHelpers = {
  /**
   * Create a vocabulary learned event
   */
  createVocabularyLearnedEvent(
    userId: string,
    languageCode: string,
    word: string,
    xpGained: number = 5
  ): ProgressEvent {
    return {
      type: 'vocabulary-learned',
      data: {
        word,
        masteryChange: 'new',
        xpGained,
        userId,
        languageCode,
        timestamp: new Date().toISOString()
      }
    };
  },

  /**
   * Create a vocabulary reviewed event
   */
  createVocabularyReviewedEvent(
    userId: string,
    languageCode: string,
    word: string,
    masteryChange: 'improved' | 'maintained' | 'declined',
    xpGained: number = 3
  ): ProgressEvent {
    return {
      type: 'vocabulary-reviewed',
      data: {
        word,
        masteryChange,
        xpGained,
        userId,
        languageCode,
        timestamp: new Date().toISOString()
      }
    };
  },

  /**
   * Create an XP gained event
   */
  createXPGainedEvent(
    userId: string,
    languageCode: string,
    xpGained: number,
    context?: { word?: string; activity?: string }
  ): ProgressEvent {
    return {
      type: 'xp-gained',
      data: {
        ...context,
        xpGained,
        userId,
        languageCode,
        timestamp: new Date().toISOString()
      }
    };
  },

  /**
   * Create a daily stats updated event
   */
  createDailyStatsUpdatedEvent(
    userId: string,
    languageCode: string
  ): ProgressEvent {
    return {
      type: 'daily-stats-updated',
      data: {
        userId,
        languageCode,
        timestamp: new Date().toISOString()
      }
    };
  }
};

/**
 * Type guards for event handling
 */
export const ProgressEventTypeGuards = {
  isVocabularyEvent(event: ProgressEvent): boolean {
    return event.type === 'vocabulary-learned' || event.type === 'vocabulary-reviewed';
  },

  isXPEvent(event: ProgressEvent): boolean {
    return event.type === 'xp-gained' || this.isVocabularyEvent(event);
  },

  isDailyStatsEvent(event: ProgressEvent): boolean {
    return event.type === 'daily-stats-updated';
  },

  isUserEvent(event: ProgressEvent, userId: string): boolean {
    return event.data.userId === userId;
  },

  isLanguageEvent(event: ProgressEvent, languageCode: string): boolean {
    return event.data.languageCode === languageCode;
  }
};

/**
 * Development/Debug utilities
 */
export const ProgressEventDebug = {
  /**
   * Monitor all events for debugging
   */
  startEventMonitoring(): UnsubscribeFunction {
    return progressEventBus.subscribe((event) => {
      console.group('🔍 Progress Event Debug');
      console.log('Type:', event.type);
      console.log('User:', event.data.userId);
      console.log('Language:', event.data.languageCode);
      console.log('Data:', event.data);
      console.log('Timestamp:', event.data.timestamp);
      console.groupEnd();
    });
  },

  /**
   * Log event statistics
   */
  logEventStats(): void {
    const history = progressEventBus.getEventHistory();
    const stats = history.reduce((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    console.table({
      'Total Events': history.length,
      'Active Subscribers': progressEventBus.getSubscriberCount(),
      ...stats
    });
  },

  /**
   * Simulate test events (for development)
   */
  emitTestEvent(userId: string = 'test-user', languageCode: string = 'es'): void {
    progressEventBus.emit(
      ProgressEventHelpers.createVocabularyLearnedEvent(
        userId,
        languageCode,
        'test-word',
        10
      )
    );
  }
};